import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { LOGOUT } from '../../constants/actions/auth.constants';
import type { AppState } from '../../reducers/root.reducer';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import AdminDashboardService from '../services/AdminDashboardService';
import IAdminProject from '../types/IAdminProject';
import AdminProductType from '../types/AdminProductType';
import IPaginationState from '../../types/state/IPaginationState';

const NAME = 'AdminDashboard';

const getProducts = createAsyncThunk(
    `${NAME}/getProducts`,
    AdminDashboardService.getProducts,
);
const getProjects = createAsyncThunk(
    `${NAME}/getProjects`,
    AdminDashboardService.getProjects,
);
const getProject = createAsyncThunk(
    `${NAME}/getProject`,
    AdminDashboardService.getProject,
);

export type AdminDashboardState = {
    products: IBaseState<AdminProductType[]>;
    projects: IPaginationState<IAdminProject[]>;
    detail: IBaseState<IAdminProject | null>;
};

const initialState: AdminDashboardState = {
    products: {
        data: [],
        loading: false,
    },
    projects: { data: [], pagination: { page: 1 } },
    detail: {
        data: null,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getProducts.pending, (state: AdminDashboardState) => {
            state.products.loading = true;
        });
        builder.addCase(
            getProducts.fulfilled,
            (state: AdminDashboardState, action) => {
                state.products.data = action.payload.data;
            },
        );

        builder.addCase(getProjects.pending, (state: AdminDashboardState) => {
            state.projects.loading = true;
        });
        builder.addCase(
            getProjects.fulfilled,
            (state: AdminDashboardState, action) => {
                state.projects.data = action.payload.data;
                state.projects.pagination = (action.payload as any).pagination;
            },
        );

        builder.addCase(getProject.pending, (state: AdminDashboardState) => {
            state.detail.loading = true;
        });
        builder.addCase(
            getProject.fulfilled,
            (state: AdminDashboardState, action) => {
                state.detail.data = action.payload.data;
            },
        );

        builder.addCase(LOGOUT, () => initialState);

        builder.addMatcher(
            isAnyOf(getProducts.fulfilled, getProducts.rejected),
            (state: AdminDashboardState) => {
                state.products.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(getProjects.fulfilled, getProjects.rejected),
            (state: AdminDashboardState) => {
                state.projects.loading = false;
            },
        );
    },
});

const state = (state: AppState) => state.admin.dashboard;
const getProductsSelector = createSelector(
    state,
    (state: AdminDashboardState) => state.products,
);
const getProductSelector = createSelector(
    [getProductsSelector, (state, id: number) => id],
    (products: IBaseState<AdminProductType[]>, id: number) =>
        products.data.find((product) => product.id === id),
);

const AdminDashboardSlice = {
    reducer: slice.reducer,
    actions: {
        getProducts,
        getProjects,
    },
    state: {
        getProducts: getProductsSelector,
        getProduct: getProductSelector,
        getProjects: createSelector(
            state,
            (state: AdminDashboardState) => state.projects,
        ),
    },
};

export default AdminDashboardSlice;
